import React, {
 createContext, useContext, useRef, ReactNode, useMemo
} from 'react';
import { FileInputValue } from '@common/types/objects';
import { EFormSubmissionCommentType } from '@modules/forms/types';

interface DraftComment {
  text: string;
  attachment: FileInputValue | null;
  commentType: EFormSubmissionCommentType;
}

interface DraftContextType {
  getDraft: (submissionId: string) => DraftComment;
  setDraft: (submissionId: string, draft: DraftComment) => void;
  clearDraft: (submissionId: string) => void;
}

const DraftContext = createContext<DraftContextType | undefined>(undefined);

interface DraftProviderProps {
  children: ReactNode;
}

export const DraftProvider: React.FC<DraftProviderProps> = ({ children }) => {
  // Use ref to store drafts without causing re-renders
  const draftsRef = useRef<Record<string, DraftComment>>({});

  const getDraft = (submissionId: string): DraftComment => {
    return draftsRef.current[submissionId] || {
      text: '',
      attachment: null,
      commentType: EFormSubmissionCommentType.COMMENT
    };
  };

  const setDraft = (submissionId: string, draft: DraftComment) => {
    draftsRef.current[submissionId] = draft;
  };

  const clearDraft = (submissionId: string) => {
    delete draftsRef.current[submissionId];
  };

  const value: DraftContextType = useMemo(() => ({
    getDraft,
    setDraft,
    clearDraft
  }), []);

  return (
    <DraftContext.Provider value={value}>
      {children}
    </DraftContext.Provider>
  );
};

export const useDraft = (): DraftContextType => {
  const context = useContext(DraftContext);
  if (context === undefined) {
    throw new Error('useDraft must be used within a DraftProvider');
  }
  return context;
};
